import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import CollectionTodayCard from "@/components/CollectionTodayCard";
import {
  Car, KeySquare, Wrench, AlertTriangle, TrendingUp,
  DollarSign, CalendarClock, Activity
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

function Kpi({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const toneMap = {
    default: "text-zinc-900",
    success: "text-green-700",
    alert: "text-red-700",
    warning: "text-amber-700",
  };
  return (
    <div className="border border-zinc-200 bg-white p-6 relative rise" data-testid={testid}>
      <div className="flex items-start justify-between mb-6">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className={`font-display font-black text-4xl tracking-tighter ${toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2 font-mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { t, fmtCurrency } = useT();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="overline">{t("common.loading")}</div>;
  }

  const fleetData = [
    { name: t("dashboard.available"), value: stats.fleet.available, color: "#16A34A" },
    { name: t("dashboard.rented"), value: stats.fleet.rented, color: "#F5A623" },
    { name: t("dashboard.maintenance"), value: stats.fleet.maintenance, color: "#D97706" },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <CollectionTodayCard />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-zinc-200">
        <div className="border-r border-b border-zinc-200 -ml-px -mt-px">
          <Kpi testid="kpi-available" icon={Car} label={t("dashboard.available")} value={stats.fleet.available} sub={t("dashboard.ofVehicles", { n: stats.fleet.total })} tone="success" />
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px">
          <Kpi testid="kpi-rented" icon={KeySquare} label={t("dashboard.rented")} value={stats.fleet.rented} sub={t("dashboard.activeContracts", { n: stats.rentals.active })} />
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px">
          <Kpi testid="kpi-maintenance" icon={Wrench} label={t("dashboard.maintenance")} value={stats.fleet.maintenance} sub={t("dashboard.inWorkshop")} tone="warning" />
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px">
          <Kpi testid="kpi-overdue" icon={AlertTriangle} label={t("dashboard.overdue")} value={stats.rentals.overdue} sub={t("dashboard.overdueReturns")} tone="alert" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-zinc-200">
        <div className="border-r border-b border-zinc-200 -ml-px -mt-px">
          <Kpi testid="kpi-revenue-month" icon={DollarSign} label={t("dashboard.revenueMonth")} value={fmtCurrency(stats.revenue.month)} sub={t("dashboard.confirmedPayments")} />
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px">
          <Kpi testid="kpi-revenue-total" icon={TrendingUp} label={t("dashboard.revenueTotal")} value={fmtCurrency(stats.revenue.total)} sub={t("dashboard.accumulated")} />
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px">
          <Kpi testid="kpi-profit" icon={Activity} label={t("dashboard.profit")} value={fmtCurrency(stats.revenue.profit)} sub={`${t("dashboard.expenses")} ${fmtCurrency(stats.revenue.expenses)}`} />
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px">
          <Kpi testid="kpi-upcoming" icon={CalendarClock} label={t("dashboard.upcoming")} value={stats.rentals.upcoming_returns} sub={t("dashboard.scheduled", { n: stats.rentals.scheduled })} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-zinc-200">
        <div className="lg:col-span-2 p-6 border-r border-zinc-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline">{t("dashboard.revenue")}</div>
              <h3 className="font-display font-bold text-xl tracking-tight">{t("dashboard.last6months")}</h3>
            </div>
            <div className="font-mono text-xs text-zinc-500">USD</div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.revenue.by_month}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="month" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#E4E4E7" }} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  cursor={{ stroke: "#F5A623", strokeDasharray: "3 3" }}
                  contentStyle={{ background: "#0A0A0A", border: "none", color: "#fff", borderRadius: 0, fontSize: 12 }}
                  formatter={(v) => [fmtCurrency(v), t("dashboard.revenue")]}
                />
                <Line type="linear" dataKey="revenue" stroke="#F5A623" strokeWidth={2} dot={{ fill: "#F5A623", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-6">
          <div className="overline mb-1">{t("dashboard.fleet")}</div>
          <h3 className="font-display font-bold text-xl tracking-tight mb-6">{t("dashboard.currentStatus")}</h3>
          {fleetData.length === 0 ? (
            <div className="text-sm text-zinc-500">{t("dashboard.noData")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={fleetData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} stroke="#fff" strokeWidth={2}>
                    {fleetData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "none", color: "#fff", borderRadius: 0, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
